import React, { MouseEventHandler, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ReactComponent as ArrowDown } from '../../../assets/svg/ArrowDown.svg';
import { getProperty, isEmpty, prop } from '../../../components/mplat/DataList/helpers';
import { RowProps, TableProps, TableRow } from '../../../components/mplat/DataList/types';
import StorageContextMenu, { MENU_HEIGHT, MENU_WIDTH } from './StorageContextMenu';


interface StorageRowProps<T> extends RowProps<T> {
    isLinkedRow?: (row: T) => boolean,
    isFolderRow?: (row: T) => boolean,
    isInProgress?: (row: T) => boolean,
    toggler?: boolean,
    additionalClass?: string,
    selected?: boolean,
    onClick?: MouseEventHandler<HTMLAnchorElement>,
    onDoubleClick?: (row: T) => void,
    onContextMenu?: (top: number, left: number) => void,
    children: React.ReactNode,
}

function Row<T>({ columns, row, selected, isLinkedRow, isFolderRow, isInProgress, onClick, onDoubleClick, onContextMenu, additionalClass, toggler, children }: StorageRowProps<T>) {
    const [show, setShow] = useState(false);

    const isLinked = isLinkedRow?.(row) ?? false;
    const isFolder = isFolderRow?.(row) ?? false;
    const inProgress = isInProgress?.(row) ?? false;

    return (
        <Link
            to={''}
            className={'storage__list-item'
                + (show ? ' _show' : '')
                + (selected ? ' _selected' : '')
                + (isLinked ? ' _mediabaza' : '')
                + (isFolder ? ' _folder' : ' _file')
                + (inProgress ? ' _in-progress' : '')
                + (additionalClass ? ` ${additionalClass}` : '')
            }
            onClick={(e) => { onClick && onClick(e); e.preventDefault(); }}
            onDoubleClick={(e) => { e.stopPropagation(); e.preventDefault(); onDoubleClick && onDoubleClick(row); }}
            onContextMenu={(e) => { e.preventDefault(); onContextMenu && onContextMenu(e.clientY, e.clientX) }}
        >
            {toggler ? <div className="storage__list-item-toggler" onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShow(!show); }} /> : null}
            {columns.map((column, i) => {
                if (column.omit) {
                    return null;
                }
                return (
                    <div key={i} className={'storage__list-col' + (column.additionalClass ? ` ${column.additionalClass}` : '')}>
                        {getProperty(row, column.selector, column.format, i)}
                    </div>
                );
            })}
            {children}
        </Link>
    );
}

const MemoRow = React.memo(Row) as <T>(props: StorageRowProps<T>) => React.JSX.Element;

interface StorageTableProps<T> extends TableProps<T> {
    selected?: string[],
    setSelected?: (v: string[]) => void,
    isLinkedRow?: (row: T) => boolean,
    isFolderRow?: (row: T) => boolean,
    isInProgress?: (row: T) => boolean,
    onDoubleClick?: (row: T) => void,
    rowAdditionalClass?: string,
    toggler?: boolean,
    withoutSort?: boolean,
    rowChild?: (row: T) => React.ReactNode,
    menuFull?: boolean,
    onDownloadZip?: (v: string) => void
    onDownload?: (v: string[]) => void,
    onShare?: (v: string) => void
    onAccess?: (v: string) => void
    onCopy?: (v: string[]) => void
    onCopyInMy?: (v: string[]) => void
    onMove?: (v: string[]) => void
    onDelete?: (v: string[]) => void
    onRename?: (v: string) => void
    onUnzip?: (v: string) => void,
    onZip?: (v: string[]) => void,
    groupBy?: (row: T) => string,
    selectionMode?: 'single' | 'multi',
    forbidDelete?: boolean,
    withoutContextMenu?: boolean,
    minWidth?: number,
}

function StorageList<T>({
    children, selected, setSelected, data,
    columns, isLinkedRow, isFolderRow, isInProgress, onDoubleClick,
    rowAdditionalClass, toggler, groupBy, withoutSort, menuFull, selectionMode, forbidDelete,
    rowChild, onDownload, onAccess, onCopy, onDelete, onMove, onShare, onRename, onUnzip, onZip, onCopyInMy,
    keyField = 'id', withoutContextMenu, onDownloadZip, minWidth, ...props
}: StorageTableProps<T>) {
    const refList = useRef<HTMLDivElement | null>(null);

    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    // const [containerPosition, setContainerPosition] = useState({ top: 0, left: 0 });
    const [menuOpen, setMenuOpen] = useState(false);

    // useEffect(() => {
    //     const rect = refList.current?.getBoundingClientRect();
    //     const top = (rect?.top || 0) + document.body.scrollTop;
    //     const left = (rect?.left || 0) + document.body.scrollLeft;
    //     setContainerPosition({ top, left });
    // }, [menuOpen]);

    const isSelected = useCallback((key: string) => {
        return selected?.includes(key) ?? false;
    }, [selected]);

    const handleShiftSelection = useCallback((i: number, key: string) => {
        if (selected && setSelected) {
            const positions = selected.map((s) => data.findIndex((r) => prop(r as TableRow, keyField) as string === s));
            const max = Math.max(...positions);
            const min = Math.min(...positions);
            const pos = i;
            if (min !== Infinity && max !== -Infinity) {
                if (pos >= min && pos <= max) {
                    if (selected.includes(key as string)) {
                        setSelected(selected.filter((s) => s !== key));
                    } else {
                        setSelected([...selected, key as string]);
                    }
                } else {
                    if (pos < min) {
                        const slice = data.slice(pos, max + 1);
                        setSelected(slice.map((z) => prop(z as TableRow, keyField) as string));
                    } else {
                        const slice = data.slice(min, pos + 1);
                        setSelected(slice.map((z) => prop(z as TableRow, keyField) as string));
                    }
                }
            } else if (min !== Infinity) {
                const slice = pos > max ? data.slice(max, pos + 1) : data.slice(pos, max + 1);
                setSelected(slice.map((z) => prop(z as TableRow, keyField) as string));
            } else if (max !== -Infinity) {
                const slice = pos > min ? data.slice(min, pos + 1) : data.slice(pos, min + 1);
                setSelected(slice.map((z) => prop(z as TableRow, keyField) as string));
            } else {
                setSelected([key as string]);
            }
        }
    }, [data, keyField, selected, setSelected]);

    useEffect(() => {
        return () => {
            setMenuOpen(false); // Закрыть меню при размонтировании
        };
    }, []);

    // Закрытие меню по Escape
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && menuOpen) {
                setMenuOpen(false);
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [menuOpen]);

    return (
        <div ref={refList} className="storage__list" {...props}>
            <div className="storage__list-header">
                <div className="storage__list-header-item">
                    {columns?.map((column, i) => (
                        <div
                            key={i}
                            className={'storage__list-col' + (column.additionalClass ? ` ${column.additionalClass}` : '')}
                            style={{ minWidth: `${minWidth !== undefined ? (minWidth ? minWidth + 'px' : undefined) : '55px'}`, cursor: ((column.onHeaderClick) ? 'pointer' : 'default') }}
                            onClick={() => column.onHeaderClick && column.onHeaderClick()}
                        >
                            {withoutSort ?
                                column.name
                                :
                                <span className="storage__list-sort _desc">
                                    {column.name} {column.onHeaderClick && <span><ArrowDown /></span>}
                                </span>
                            }
                        </div>
                    ))}
                </div>
            </div>
            {data?.map((row, i) => {
                const thisGroup = groupBy && groupBy(row);
                let group = undefined;
                if (groupBy && thisGroup !== undefined) {
                    if (i === 0) {
                        group = thisGroup;
                    } else {
                        const previousValue = data[i - 1];
                        const prevGroup = groupBy(previousValue);
                        if (prevGroup !== thisGroup) {
                            group = thisGroup;
                        }
                    }
                }

                const key = prop(row as TableRow, keyField) as string | number;
                const id = isEmpty(key) ? i : key;
                return (
                    <React.Fragment key={id}>
                        {group && <div className="storage__files-heading">{group}</div>}
                        <MemoRow
                            key={id}
                            columns={columns}
                            row={row}
                            toggler={toggler}
                            selected={isSelected(key as string)}
                            isLinkedRow={isLinkedRow}
                            isFolderRow={isFolderRow}
                            isInProgress={isInProgress}
                            additionalClass={rowAdditionalClass}
                            onClick={(e) => {
                                if (selected && setSelected) {
                                    if ((e.ctrlKey || e.metaKey) && selectionMode !== 'single') {
                                        setSelected(selected.includes(key as string) ? selected.filter((s) => s !== key) : [...selected, key as string])
                                    } else if (e.shiftKey && selectionMode !== 'single') {
                                        handleShiftSelection(i, key as string);
                                    } else {
                                        if (selected.length > 1) {
                                            setSelected([key as string])
                                        } else {
                                            setSelected(selected.includes(key as string) ? [] : [key as string])
                                        }
                                    }
                                }
                            }}
                            onDoubleClick={onDoubleClick}
                            onContextMenu={(t, l) => {
                                if (withoutContextMenu) return;
                                if (selected && selected.length < 2) {
                                    setSelected && setSelected([key as string]);
                                }
                                setMenuOpen(true);
                                setMenuPosition({ top: t, left: l });
                            }}
                        >
                            {rowChild && rowChild(row)}
                        </MemoRow>
                    </React.Fragment>
                )
            })}
            {/* {selected && menuOpen && <StorageContextMenu
                menuFull={menuFull}
                selected={selected}
                top={menuPosition.top - containerPosition.top}
                left={menuPosition.left - containerPosition.left}
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
                onDownload={onDownload}
                onAccess={onAccess}
                onCopy={onCopy}
                onDelete={onDelete}
                onMove={onMove}
                onRename={onRename}
                onShare={onShare}
                onUnzip={onUnzip}
                onZip={onZip}
                className={(refList.current?.scrollWidth || 0) < (menuPosition.left - containerPosition.left + 178) ? '_left' : ''}
            />} */}

            {selected && menuOpen && createPortal(<StorageContextMenu
                menuFull={menuFull}
                selected={selected}
                forbidDelete={forbidDelete}
                top={menuPosition.top}
                left={menuPosition.left}
                isOpen={menuOpen}
                onClose={() => setMenuOpen(false)}
                onDownloadZip={onDownloadZip}
                onDownload={onDownload}
                onAccess={onAccess}
                onCopy={onCopy}
                onCopyInMy={onCopyInMy}
                onDelete={onDelete}
                onMove={onMove}
                onRename={onRename}
                onShare={onShare}
                onUnzip={onUnzip}
                onZip={onZip}
                className={`
                    ${window.innerWidth < menuPosition.left + MENU_WIDTH ? '_left' : ''}
                    ${window.innerHeight < menuPosition.top + MENU_HEIGHT ? '_top' : ''}
                `}
            />, document.body)}

            {children}
        </div>
    );
}

export default StorageList;
